/**
 * End-of-demo contact email (backend: apps/backend/src/demo-lead). Plain
 * fetch without credentials: the form runs for anonymous demo users and
 * must not send cookies or the demo session.
 */

/** Same origin rule as baseQuery and sso.ts, without importing the store. */
const getApiOrigin = (): string => {
  const configured = process.env.NODE_ENV === 'development' ? process.env.REACT_APP_DEVELOPMENT_BASE_URL : process.env.REACT_APP_PRODUCTION_BASE_URL;
  if (!configured || configured === 'auto') return window.location.origin;
  return configured.replace(/\/$/, '');
};

/**
 * Id of the consent line the form shows (`demo.lead.consent`). Must match
 * DEMO_LEAD_CONSENT_VERSION in the backend; bump both when the text changes.
 */
export const DEMO_LEAD_CONSENT_VERSION = 'demo-lead-consent-2026-10-27';

/** UI languages the backend knows; anything else is off without asking. */
const DEMO_LEAD_UI_LOCALES = ['nl', 'en', 'de'];

/** Base language of the UI locale ('en-GB' -> 'en'), or null when unknown. */
export const demoLeadUiLocale = (language: string | undefined): string | null => {
  const base = (language || '').trim().split(/[-_]/)[0].toLowerCase();
  return DEMO_LEAD_UI_LOCALES.includes(base) ? base : null;
};

const statusPromises = new Map<string, Promise<boolean>>();

/**
 * Is the form on for this UI locale? The backend decides per locale
 * (DEMO_LEAD_ALLOWED_LOCALES: NL and EN, not DE until its privacy text is
 * reviewed). GET /demo-leads/status?locale= once per locale per page load;
 * an unknown locale or any failure reads as off. The consent line and the
 * confirmation promise a follow-up mail, so the form also stays off unless
 * the backend says that mail's copy is approved (followupCopyApproved).
 */
export const fetchDemoLeadCaptureEnabled = (language: string | undefined): Promise<boolean> => {
  const locale = demoLeadUiLocale(language);
  if (!locale) return Promise.resolve(false);
  let promise = statusPromises.get(locale);
  if (!promise) {
    promise = (async () => {
      try {
        if (typeof fetch !== 'function') return false;
        const response = await fetch(`${getApiOrigin()}/api/v1/demo-leads/status?locale=${encodeURIComponent(locale)}`, { credentials: 'omit' });
        if (!response.ok) return false;
        const body = await response.json();
        return body?.enabled === true && body?.followupCopyApproved === true;
      } catch {
        return false;
      }
    })();
    statusPromises.set(locale, promise);
  }
  return promise;
};

/** Tests only. */
export const resetDemoLeadStatusCache = () => {
  statusPromises.clear();
};

/** 'error': network failure, 404 (locale not on the allowlist) or 5xx. 'rateLimited': HTTP 429. */
export type DemoLeadSubmitOutcome = 'ok' | 'invalid' | 'rateLimited' | 'error';

export const submitDemoLead = async (input: { email: string; locale: string; website: string }): Promise<DemoLeadSubmitOutcome> => {
  try {
    const response = await fetch(`${getApiOrigin()}/api/v1/demo-leads`, {
      method: 'POST',
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: input.email,
        locale: input.locale,
        consentTextVersion: DEMO_LEAD_CONSENT_VERSION,
        website: input.website,
      }),
    });
    if (response.status === 202 || response.ok) return 'ok';
    if (response.status === 400) return 'invalid';
    if (response.status === 429) return 'rateLimited';
    return 'error';
  } catch {
    return 'error';
  }
};
